import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Message from 'components/Message';
import LINKS from 'constants/links';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';
import {
  formatDayList,
  formatWindowTimes,
  groupWindowsByDay,
  selectEffectiveWindows,
} from './PeakTimeDefinition.helpers';
import { usePeakDefinitionsQuery } from './PeakTimeDefinition.hooks';
import { getPeakTimeMessage } from './SmartTools.helpers';

import './PeakTimeDefinition.scss';

export const messages = defineMessages({
  peakTimeUSCA: {
    id: 'manage.smart-tools.peak-definition-us-ca',
    defaultMessage:
      '<strong>Peak times:</strong> Monday to Friday from 6:00-9:59 AM and 5:00-6:59 PM, and Saturday and Sunday from 8:00-10:59 AM in the local time zone. <a>Learn more</a>',
  },
  peakTimeNonUSCA: {
    id: 'manage.smart-tools.peak-definition-non-us-ca',
    defaultMessage:
      '<strong>Peak times:</strong> Monday to Friday from 6:00-9:59 AM, 12:00-12:59 PM, and 5:00-7:59 PM, and Saturday and Sunday from 8:00-10:59 AM in the local time zone. <a>Learn more</a>',
  },
  offPeakTime: {
    id: 'manage.smart-tools.off-peak-definition',
    defaultMessage:
      '<strong>Off-peak times:</strong> All classes scheduled to start at any time other than Peak times. <a>Learn more</a>',
  },
  peakTimesHeading: {
    id: 'manage.smart-tools.peak-definition-heading',
    defaultMessage: '<strong>Peak times</strong> (local time zone)',
  },
  peakTimesLoading: {
    id: 'manage.smart-tools.peak-definition-loading',
    defaultMessage: 'Loading peak times',
  },
});

const { block, element } = bem('peak-time-definition');

const richText = {
  strong: text => <strong>{text}</strong>,
  a: text => (
    <a href={LINKS.termsAdditionalDefinitions.href} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  ),
};

const scheduleGroupShape = PropTypes.shape({
  days: PropTypes.arrayOf(PropTypes.number).isRequired,
  windows: PropTypes.arrayOf(
    PropTypes.shape({
      start_time: PropTypes.shape({ hours: PropTypes.number, minutes: PropTypes.number }),
      end_time: PropTypes.shape({ hours: PropTypes.number, minutes: PropTypes.number }),
    })
  ).isRequired,
});

const PeakTimeSkeleton = () => {
  const intl = useIntl();

  return (
    <div className={element('skeleton')} role="status" aria-label={intl.formatMessage(messages.peakTimesLoading)}>
      <span className={element('skeleton-bar')} />
      <span className={element('skeleton-bar')} />
      <span className={element('skeleton-bar')} />
    </div>
  );
};

const PeakTimeSchedule = ({ groups }) => {
  const intl = useIntl();

  return (
    <>
      <Message as="div" className={element('schedule-heading')} message={messages.peakTimesHeading} values={richText} />
      <dl className={element('schedule')}>
        {groups.map(({ days, windows }) => (
          <React.Fragment key={days.join('-')}>
            <dt className={element('schedule-days')}>{formatDayList(intl, days)}</dt>
            <dd className={element('schedule-times')}>{formatWindowTimes(intl, windows)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </>
  );
};

PeakTimeSchedule.propTypes = {
  groups: PropTypes.arrayOf(scheduleGroupShape).isRequired,
};

const PeakTimeDefinition = ({ countryCode = 'USA' }) => {
  const { venueId } = useSelector(selectActiveVenue);
  const { data, isInitialLoading } = usePeakDefinitionsQuery(venueId);

  const handleLearnMore = () => {
    Segment.event('Peak Off-peak Learn More', {
      venue_id: venueId,
    });
  };

  const renderPeakTimes = () => {
    if (isInitialLoading) {
      return <PeakTimeSkeleton />;
    }

    const groups = groupWindowsByDay(selectEffectiveWindows(data));

    if (groups.length) {
      return <PeakTimeSchedule groups={groups} />;
    }

    return (
      <Message
        as="div"
        className={element('peak-time-message')}
        message={getPeakTimeMessage(countryCode, messages)}
        values={richText}
      />
    );
  };

  return (
    <div className={block()}>
      {renderPeakTimes()}
      <Message
        as="div"
        className={element('off-peak-time-message')}
        message={messages.offPeakTime}
        onClick={handleLearnMore}
        values={richText}
      />
    </div>
  );
};

PeakTimeDefinition.propTypes = {
  countryCode: PropTypes.string,
};

export default PeakTimeDefinition;
